/**
 * Date formatting utilities - displays dates in user's local browser timezone
 * Author: Markus.van.Kempen@gmail.com
 */

/**
 * Format date/time in user's local timezone
 * @param {string|Date} dateString - Date string or Date object
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in user's local time
 */
export const formatDateAtlantic = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  
  // Handle SQLite DATETIME format (YYYY-MM-DD HH:MM:SS) - treat as UTC and convert to local time
  let date;
  if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
    // SQLite format without timezone - treat as UTC
    date = new Date(dateString.replace(' ', 'T') + 'Z');
  } else {
    date = new Date(dateString);
  }
  
  if (isNaN(date.getTime())) return 'N/A';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options
  };
  
  return date.toLocaleString('en-CA', defaultOptions);
};

/**
 * Format date only (no time) in user's local timezone
 * @param {string|Date} dateString - Date string or Date object
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateOnlyAtlantic = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  
  // Handle SQLite DATETIME format (YYYY-MM-DD HH:MM:SS) - treat as UTC and convert to local time
  let date;
  if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
    // SQLite format without timezone - treat as UTC
    date = new Date(dateString.replace(' ', 'T') + 'Z');
  } else {
    date = new Date(dateString);
  }
  
  if (isNaN(date.getTime())) return 'N/A';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  return date.toLocaleDateString('en-CA', defaultOptions);
};

/**
 * Format time only in user's local timezone
 * @param {string|Date} dateString - Date string or Date object
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted time string
 */
export const formatTimeOnlyAtlantic = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  
  // Handle SQLite DATETIME format (YYYY-MM-DD HH:MM:SS) - treat as UTC and convert to local time
  let date;
  if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
    // SQLite format without timezone - treat as UTC
    date = new Date(dateString.replace(' ', 'T') + 'Z');
  } else {
    date = new Date(dateString);
  }
  
  if (isNaN(date.getTime())) return 'N/A';
  
  const defaultOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options
  };
  
  return date.toLocaleTimeString('en-CA', defaultOptions);
};

/**
 * Format date with full date and time in user's local timezone
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date and time string
 */
export const formatDateTimeAtlantic = (dateString) => {
  return formatDateAtlantic(dateString, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

/**
 * Format date for display in order lists (compact format) - uses user's local timezone
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date string
 */
const parseOrderDateInput = (dateString) => {
  if (!dateString) return null;
  if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
    const date = new Date(dateString.replace(' ', 'T') + 'Z');
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatOrderDate = (dateString) => {
  if (!dateString) return 'N/A';

  const date = parseOrderDateInput(dateString);
  if (!date) return dateString || 'N/A';

  return date.toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/** ISO-like date for CSV export/import (no commas). */
export const formatOrderDateForCsv = (dateString) => {
  const date = parseOrderDateInput(dateString);
  if (!date) return String(dateString || '').trim();
  return date.toISOString().replace('T', ' ').slice(0, 19);
};

/** Parse date from CSV import (ISO/SQLite or legacy display format). */
export const parseOrderDateFromCsv = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(raw)) {
    const normalized = raw.includes('T') ? raw : `${raw.replace(' ', 'T')}Z`;
    const date = new Date(normalized);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().replace('T', ' ').slice(0, 19);
    }
  }

  const date = new Date(raw);
  if (!Number.isNaN(date.getTime())) {
    return date.toISOString().replace('T', ' ').slice(0, 19);
  }
  return null;
};

/**
 * Get current date/time in Atlantic Time Zone as ISO string
 * @returns {string} ISO string in Atlantic Time
 */
export const getCurrentAtlanticTime = () => {
  const now = new Date();
  return now.toLocaleString('en-CA', {
    timeZone: 'America/Halifax',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

